import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
type FAQItem = {
  question: string;
  answer: string;
  category: 'general' | 'catchup' | 'monthly' | 'startup' | 'tax';
};
export function FAQ() {
  const [activeCategory, setActiveCategory] = useState<string>('general');
  const [openItems, setOpenItems] = useState<number[]>([]);
  const toggleItem = (index: number) => {
    if (openItems.includes(index)) {
      setOpenItems(openItems.filter(item => item !== index));
    } else {
      setOpenItems([...openItems, index]);
    }
  };
  const faqs: FAQItem[] = [
  // General FAQs
  {
    question: 'What bookkeeping software do you work with?',
    answer: "I work with QuickBooks® Online. If you use a different software, let's discuss during our consultation call.",
    category: 'general'
  }, {
    question: 'Do I need to have existing bookkeeping software?',
    answer: 'No, I can help you select and set up the right software for your business needs as part of our startup services.',
    category: 'general'
  }, {
    question: 'How do we share documents securely?',
    answer: 'I use secure, encrypted file-sharing platforms to exchange sensitive financial information and documents.',
    category: 'general'
  },
  // Catch-up Bookkeeping FAQs
  {
    question: 'How far back can you help with catch-up bookkeeping?',
    answer: "I can help with bookkeeping catch-up for any time period, whether it's a few months or several years.",
    category: 'catchup'
  }, {
    question: "What if I'm missing some receipts or documents?",
    answer: "We'll work with what you have and implement best practices to reconstruct missing information from bank and credit card statements.",
    category: 'catchup'
  }, {
    question: 'What happens after catch-up is complete?',
    answer: 'Many clients transition to our monthly bookkeeping service to stay current. We provide training and procedures for self-maintenance.',
    category: 'catchup'
  },
  // Monthly Bookkeeping FAQs
  {
    question: "What's included in monthly bookkeeping services?",
    answer: 'Monthly services include transaction categorization, account reconciliation, and financial statement preparation.',
    category: 'monthly'
  }, {
    question: 'What if I have questions between monthly reviews?',
    answer: 'Quick questions are included in your service. For detailed consultations, we offer additional hours at our standard rate.',
    category: 'monthly'
  }, {
    question: 'Do you handle payroll?',
    answer: 'We can do payroll as an add on to Monthly Bookkeeping services.',
    category: 'monthly'
  }, {
    question: 'Do you handle sales tax reporting?',
    answer: 'We can do sales tax reporting only in the state of Iowa, as an add on to Monthly Bookkeeping services.',
    category: 'monthly'
  },
  // Startup Setup FAQs
  {
    question: "I'm just starting my business. When should I set up bookkeeping?",
    answer: 'The best time is right away! Setting up proper bookkeeping from day one saves time and money in the long run and helps establish good financial habits.',
    category: 'startup'
  }, {
    question: 'Will you train me to do my own bookkeeping?',
    answer: "Yes, if that's your goal. I offer training packages to teach you the basics of maintaining your books, with ongoing support as needed.",
    category: 'startup'
  }, {
    question: 'How long does the setup process take?',
    answer: 'Initial setup typically takes 3-5 business days. Training sessions are scheduled at your convenience within the following week.',
    category: 'startup'
  },
  // Tax Preparation FAQs
  {
    question: 'What documents do I need to provide for tax prep?',
    answer: "You will receive a checklist, but generally you'll need income documents (W-2s, 1099s), expense records, prior-year return, and any forms related to deductions or credits (like childcare or mortgage interest).",
    category: 'tax'
  }, {
    question: 'I own a small business. Do I need to have my books done before you prepare my taxes?',
    answer: 'Clean books are key to accurate tax filing. If you need help getting your books in order, I offer clean-up services before we start the return.',
    category: 'tax'
  }, {
    question: 'Do you offer e-filing?',
    answer: 'Yes! I file all eligible returns electronically and provide confirmation once your return is accepted by the IRS and state.',
    category: 'tax'
  }];
  const filteredFAQs = faqs.filter(faq => faq.category === activeCategory);
  return <section id="faq" className="py-16 bg-white" data-id="element-185" data-mp="vg5lq8bw9a6x">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-186" data-mp="vfe0nrfxauuw">
        <div className="text-center mb-12" data-id="element-187" data-mp="372zdzvfefvsi">
          <h2 className="text-3xl md:text-4xl font-bold text-[#3A4A46] mb-4" data-id="element-188" data-mp="371y2xqydv9as">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-189" data-mp="2x5cwr2amns5d">
            Find answers to common questions about our bookkeeping and tax
            services
          </p>
        </div>
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center mb-8" data-id="element-190" data-mp="3pth4dtcrefap">
          <button onClick={() => setActiveCategory('general')} className={`px-4 py-2 mx-2 mb-2 rounded-md ${activeCategory === 'general' ? 'bg-[#3A4A46] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-191" data-mp="3prxnq1n6y28e">
            General
          </button>
          <button onClick={() => setActiveCategory('catchup')} className={`px-4 py-2 mx-2 mb-2 rounded-md ${activeCategory === 'catchup' ? 'bg-[#3A4A46] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-192" data-mp="3kwqnj3t4rfo8">
            Catch-Up Bookkeeping
          </button>
          <button onClick={() => setActiveCategory('monthly')} className={`px-4 py-2 mx-2 mb-2 rounded-md ${activeCategory === 'monthly' ? 'bg-[#3A4A46] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-193" data-mp="fymbke4u609o">
            Monthly Bookkeeping
          </button>
          <button onClick={() => setActiveCategory('startup')} className={`px-4 py-2 mx-2 mb-2 rounded-md ${activeCategory === 'startup' ? 'bg-[#3A4A46] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-194" data-mp="16186idv4lf5">
            Startup Setup
          </button>
          <button onClick={() => setActiveCategory('tax')} className={`px-4 py-2 mx-2 mb-2 rounded-md ${activeCategory === 'tax' ? 'bg-[#3A4A46] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-195" data-mp="3jfbjo4k2hi1g">
            Tax Preparation
          </button>
        </div>
        {/* FAQ Accordion */}
        <div className="max-w-3xl mx-auto" data-id="element-196" data-mp="1l4kvj5m41jw7">
          {filteredFAQs.map((faq, index) => <div key={index} className="mb-4 border border-gray-200 rounded-lg overflow-hidden" data-id="element-197" data-mp="1p54qc2j7obf6" data-mpi={index}>
              <button onClick={() => toggleItem(index)} className="w-full text-left p-4 focus:outline-none flex justify-between items-center bg-gray-50 hover:bg-gray-100" data-id="element-198" data-mp="1twwk0lxnyx9j">
                <span className="font-medium text-[#3A4A46]" data-id="element-199" data-mp="1279wnm4e4sha">
                  {faq.question}
                </span>
                {openItems.includes(index) ? <ChevronUpIcon className="h-5 w-5 text-[#8A9A5B]" data-id="element-200" data-mp="1b53q8part80r" /> : <ChevronDownIcon className="h-5 w-5 text-[#8A9A5B]" data-id="element-201" data-mp="1g5zrsw7ngi30" />}
              </button>
              {openItems.includes(index) && <div className="p-4 bg-white" data-id="element-202" data-mp="2rk4zo0azhc3m">
                  <p className="text-gray-700" data-id="element-203" data-mp="2ritv3y9ps0bg">{faq.answer}</p>
                </div>}
            </div>)}
        </div>
        <div className="text-center mt-10" data-id="element-204" data-mp="3clt9m5rvv55i">
          <p className="text-gray-600 mb-4" data-id="element-205" data-mp="38nlu1e95o9vw">
            Don't see your question here? Contact me directly.
          </p>
          <a href="/contact" className="inline-block bg-[#3A4A46] text-white px-5 py-2 rounded hover:bg-[#8A9A5B] transition-colors" data-id="element-206" data-mp="3mfjwx7c9x9jm">
            Get in Touch
          </a>
        </div>
      </div>
    </section>;
}